import { useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ChevronDown, Menu } from 'lucide-react'
import { StaffAvatar } from '@/components/staff-avatar'
import { useCurrentUser } from '@/api/hooks'
import { CommandMenu } from '@/components/command-menu'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type HeaderProps = {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, signOut } = useAuth()
  const { data: currentUser } = useCurrentUser()
  const { showMyProjects, setShowMyProjects } = useMyProjectsFilter()
  const [menuOpen, setMenuOpen] = useState(false)

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??'

  return (
    // The top padding keeps the bar below an iPhone's status bar in the home-screen app.
    <header className="flex h-[calc(3.5rem+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-border bg-background px-3 pt-[env(safe-area-inset-top)] md:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 md:hidden"
        onClick={onMenuClick}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      <div className="min-w-0 flex-1">
        <CommandMenu />
      </div>

      <div className="flex shrink-0 items-center gap-1.5 md:gap-3">
        <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
          <button
            className={cn(
              'rounded px-2 py-1 text-xs font-medium transition-colors md:px-2.5',
              showMyProjects
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setShowMyProjects(true)}
          >
            <span className="hidden sm:inline">My Projects</span>
            <span className="sm:hidden">Mine</span>
          </button>
          <button
            className={cn(
              'rounded px-2 py-1 text-xs font-medium transition-colors md:px-2.5',
              !showMyProjects
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setShowMyProjects(false)}
          >
            All
          </button>
        </div>

        <PickerMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <PickerMenuTrigger asChild>
            <Button variant="ghost" className="flex h-8 items-center gap-1.5 px-1.5 md:gap-2 md:px-2">
              <StaffAvatar
                name={user?.name}
                photoUrl={currentUser?.photo_url}
                className="h-7 w-7"
                fallbackClassName="text-[10px]"
              >
                {initials}
              </StaffAvatar>
              <span className="hidden max-w-[160px] truncate text-sm text-foreground lg:inline-block">
                {user?.email}
              </span>
              <ChevronDown className="hidden h-3 w-3 text-muted-foreground md:inline-block" />
            </Button>
          </PickerMenuTrigger>
          <PickerMenuContent title={user?.name ?? 'Account'} align="end" className="w-56">
            <PickerMenuList className="pt-0">
              <p className="truncate px-2 pb-1.5 text-xs text-muted-foreground">{user?.email}</p>
              <PickerMenuSeparator />
              <PickerMenuItem
                onSelect={() => {
                  setMenuOpen(false)
                  signOut()
                }}
              >
                Log out
              </PickerMenuItem>
            </PickerMenuList>
          </PickerMenuContent>
        </PickerMenu>
      </div>
    </header>
  )
}
